import { useEffect, useState } from "react";
import type { ReactNode } from "react";

// Collapsible section in the box-flat cream pattern: a chevron + title row that
// toggles a divided body. State is encoded by the chevron rotation alone.
// Open/close animates via the 0fr→1fr grid-rows trick at --duration-base; the
// body therefore stays mounted while collapsed (state survives a collapse) and
// is marked inert so hidden controls can't be tabbed into.
//
// Supports both modes:
//   uncontrolled (defaultOpen) — multiple can be open simultaneously
//   controlled  (open + onToggle) — caller decides; e.g. single-open accordion

interface DisclosureProps {
  title: ReactNode;
  count?: number | string | null;
  headerAction?: ReactNode;
  open?: boolean;
  onToggle?: () => void;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

export function Disclosure({
  title,
  count = null,
  headerAction,
  open: openProp,
  onToggle,
  defaultOpen = false,
  children,
  className = "",
}: DisclosureProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;
  // Nach abgeschlossener Aufklapp-Animation den Overflow freigeben, damit Popover/Tooltips
  // im Body (z.B. SkillLevelInfoIcon) nicht an der Box-Kante abgeschnitten werden. Waehrend
  // der Animation und im zugeklappten Zustand muss overflow-hidden bleiben (0fr-Trick).
  const [settled, setSettled] = useState(open);
  useEffect(() => {
    if (!open) setSettled(false);
  }, [open]);
  const clip = !(open && settled);

  function toggle() {
    if (isControlled) onToggle?.();
    else setOpenState((v) => !v);
  }

  return (
    <div className={`bg-page box-flat ${clip ? "overflow-hidden" : ""} ${className}`}>
      {headerAction ? (
        <div className="hover:bg-ink/[0.06] flex w-full items-center gap-2 transition">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 px-5 py-3 text-left"
          >
            <span
              className={`shrink-0 text-[10px] transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}
            >
              ›
            </span>
            <span className="min-w-0 text-sm font-medium">{title}</span>
            {count != null ? <span className="shrink-0 text-[11px] text-ink/45">({count})</span> : null}
          </button>
          <div className="flex shrink-0 items-center py-2 pr-5">{headerAction}</div>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="hover:bg-ink/[0.06] flex w-full items-center justify-between gap-2 px-5 py-3 text-left transition"
        >
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}
            >
              ›
            </span>
            <span className="text-sm font-medium">{title}</span>
            {count != null ? <span className="text-[11px] text-ink/45">({count})</span> : null}
          </div>
        </button>
      )}
      <div
        inert={!open}
        onTransitionEnd={(e) => {
          // Nur die eigene grid-rows-Transition zaehlt (Body-Transitions bubbeln hoch).
          if (e.target === e.currentTarget && open) setSettled(true);
        }}
        className={`grid transition-[grid-template-rows] duration-[var(--duration-base)] ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className={clip ? "overflow-hidden" : ""}>
          <div className="border-t border-ink/5 px-5 py-5">{children}</div>
        </div>
      </div>
    </div>
  );
}
